import React from 'react';
import { useTranslation } from 'react-i18next';

import { RoleContext, apiOperation } from '@/app/api/common';
import { checkNoError } from '@/features/channels/channelsSlice';

import {
  CHECK_INTERVAL_DEFAULT,
  CHECK_TIMEOUT_DEFAULT,
  EXPECT_STATUS_DEFAULT,
  MONITORING_POLL_MS,
  MonitoringCheck,
  MonitoringConfig,
  MonitoringStatus,
  REALERT_HOURS_DEFAULT,
  SKYWAY_PROBE_INTERVAL_DEFAULT,
  SKYWAY_PROBE_TIMEOUT_MIN,
  buildMonitoringPayload,
  copyMonitoringConfig,
  copyMonitoringStatus,
  monitoringConfigUrl,
  monitoringStatusUrl,
  monitoringTestAlertUrl,
  monitoringTestTicketsUrl,
} from '@/features/monitoring/monitoringApi';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import { FieldInfo } from '@/components/Common/Dropdown';

import { formatWhen, startStatusPoll } from '@/pages/Settings/epgSources';

import {
  AddRow,
  GroupDivider,
  InfoBanner,
  Pill,
  SettingsCard,
  SettingsInput,
  SettingsSelect,
  ToggleRow,
  su,
} from '@/pages/Settings/settingsUi';

import {
  CustomCheckEdit,
  EmailEdit,
  FAMILY_ICON,
  LOG_FORMAT_JSON,
  LOG_FORMAT_TEXT,
  LOG_FORMATS_SHOWN,
  LOG_LEVEL_NORMAL,
  LOG_LEVEL_VERBOSE,
  LOG_LEVELS_SHOWN,
  MonitoringForm,
  TestOutcome,
  VERDICT_ICON,
  VERDICT_TONE,
  bodyContainsUnreachable,
  buildSection,
  checkInstance,
  checkRowErrors,
  checkState,
  checkTone,
  emailIsUrl,
  extraStoredOption,
  formChanged,
  formInvalid,
  groupChecks,
  intFieldInvalid,
  knownKind,
  logKeyPayload,
  mentionErrors,
  messageIsQualified,
  newCheckEdit,
  newEmailEdit,
  nobodyTold,
  pageVerdict,
  savedHasTeams,
  savedHasTickets,
  secondsSince,
  serverLabel,
  serverNameError,
  skippedEmails,
  testOutcome,
  ticketsErrors,
  toForm,
  tokenUntouched,
  webhookError,
  webhookUntouched,
  worstTone,
} from '@/pages/Settings/monitoringUi';

// A saved change applies within one 15-second sweep, so a check whose interval
// was shortened — or one that was just added — appears on its own shortly after
// the save. Look again once, rather than making the admin wait for the next
// poll tick to see their own edit take effect.
const SAVE_RECHECK_MS = 5000;

const EMPTY_FORM: MonitoringForm = {
  emails:			[],
  realertHours:		'',
  disabled:			false,
  intervalSeconds:	'',
  timeoutSeconds:	'',
  checks:			[],
  logLevel:			LOG_LEVEL_NORMAL,
  logFormat:		LOG_FORMAT_TEXT,
  serverName:		'',
  teamsWebhook:		'',
  teamsEnabled:		true,
  mentions:			[],
  ticketsUrl:		'',
  ticketsToken:		'',
  ticketsProject:	'',
  ticketsEnabled:	true,
};

type ConfigError = '' | 'forbidden' | 'unavailable' | 'broken' | 'failed';

type TestChannel = '' | 'teams' | 'tickets';

// ---------------------------------------------------------------------------
// Health — one row per check
// ---------------------------------------------------------------------------

const HealthRow = (props: { row: MonitoringCheck }) => {
  const { t } = useTranslation();

  const row = props.row;
  const state = checkState(row);
  const instance = checkInstance(row.check);
  const known = knownKind(row.kind);

  // NEVER the raw `check` string as a title — `kpn:KPN-1:auth` is a log
  // identifier, not a label. It stays visible underneath as small secondary
  // text so a row can be matched against the server log.
  const title =	row.kind === 'http'
  				?	(instance || t('monitoring:kind:http:name'))
  				:	known
  						?	t('monitoring:kind:' + row.kind + ':name')
  						:	(instance || row.kind);

  const suffix = row.kind === 'http' ? '' : instance;

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full rounded-[8px] border-solid border-1 border-[#E6E9EF] px-[16px] py-[14px] gap-[8px]">

    <FlexRow align="start" className="gap-[10px] w-full flex-wrap">
      <FlexRow direction="column" align="start" flex={1} className="gap-[2px] min-w-[180px]">
        <div>
          <span className="text-[15px] font-semibold text-[#1F2733]">{ title }</span>
          { suffix &&
            <span className={su.titleSoft + ' !text-[14px]'}> · { suffix }</span>
          }
        </div>
        <span className="text-[11px] text-[#9AA2B1] font-mono">{ row.check }</span>
      </FlexRow>

      <FlexRow flex={0} className="gap-[6px] flex-wrap justify-end">
        <Pill tone={checkTone(row)} dot={true} label={t('monitoring:state:' + state)} />

        {/* The DEBOUNCED verdict, and the only field email is derived from. A
            row can be failing without alerting yet — saying so stops "why did
            nobody get an email" from becoming a support call. */}
        { row.alerting &&
          <Pill tone="bad" icon="notifications_active" label={t('monitoring:row:alerting')} />
        }

        {/* 0 ms is what a PASSIVE check reports — it means "not measured", not
            "instant", so it is rendered as nothing at all. */}
        { row.latency_ms > 0 &&
          <Pill tone="info" label={t('monitoring:row:latency', { ms: row.latency_ms })} />
        }

        { row.consecutive_failures > 0 &&
          <Pill tone="warn" label={t('monitoring:row:failures', { count: row.consecutive_failures })} />
        }
      </FlexRow>
    </FlexRow>

    { row.detail &&
      <span className="text-[13px] leading-[18px] text-[#5A6474]">{ row.detail }</span>
    }

    { row.err &&
      <span className="text-[12px] leading-[16px] text-[#B14545]">
        { t('monitoring:row:error_label') } { row.err }
      </span>
    }

    {/* Remediation only when there is something to remediate. On a healthy row
        it is noise that trains admins to skip the text. */}
    { state !== 'healthy' && known &&
      <InfoBanner text={t('monitoring:kind:' + row.kind + ':fix')} />
    }

    {/* The one failure nobody is emailed about: when SMTP is down the alert
        cannot be delivered, so this check is status-page-and-log only. */}
    { row.kind === 'mailer' &&
      <span className={su.hint}>{ t('monitoring:row:mailer_note') }</span>
    }

    <FlexRow className="gap-[16px] w-full flex-wrap">
      <span className={su.hint}>
        { row.ran_at
            ? t('monitoring:row:ran_at', { when: formatWhen(row.ran_at) })
            : t('monitoring:row:never_ran') }
      </span>
      <span className={su.hint}>
        { row.last_ok
            ? t('monitoring:row:last_ok', { when: formatWhen(row.last_ok) })
            : t('monitoring:row:never_ok') }
      </span>
      { row.alerting && row.last_change &&
        <span className={su.hint}>
          { t('monitoring:row:since', { when: formatWhen(row.last_change) }) }
        </span>
      }
      { row.last_alert_at &&
        <span className={su.hint}>
          { t('monitoring:row:last_alert', { when: formatWhen(row.last_alert_at) }) }
        </span>
      }
    </FlexRow>
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------
// Settings — one operator-added service
// ---------------------------------------------------------------------------

interface CheckRowProps {
  row:			CustomCheckEdit;
  index:		number;
  all:			CustomCheckEdit[];
  triedSave:	boolean;
  update:		(patch: Partial<CustomCheckEdit>) => any;
  remove:		() => any;
}

const CustomCheckRow = (props: CheckRowProps) => {
  const { t } = useTranslation();

  const row = props.row;
  const errs = checkRowErrors(row, props.index, props.all);
  const show = props.triedSave;

  const methodOptions: FieldInfo[] = [
    { value: 'GET',  desc: 'GET' },
    { value: 'HEAD', desc: 'HEAD' },
  ];

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full rounded-[8px] border-solid border-1 border-[#E6E9EF]">

    <FlexRow className="px-[16px] py-[12px] gap-[10px] w-full">
      <span className="text-[15px] font-semibold text-[#1F2733] whitespace-nowrap">
        { row.name.trim() || t('monitoring:checks:untitled') }
      </span>
      <Pill tone="info" label={row.type.toUpperCase()} />
      <FlexRow flex={1} />
      <FlexRow flex={0}
               className="gap-[6px] cursor-pointer select-none"
               onClick={() => props.remove()}>
        <span className="material-icons !text-[16px] !text-[#D64545]">delete_outline</span>
        <span className="text-[13px] font-medium text-[#D64545]">
          { t('monitoring:checks:remove') }
        </span>
      </FlexRow>
    </FlexRow>

    <FlexRow direction="column"
             align="start"
             className="px-[16px] pt-[18px] pb-[16px] gap-[16px] w-full border-t-1 border-solid border-[#EEF0F4]">

      <SettingsInput label={t('monitoring:checks:name')}
                     required={true}
                     placeholder="core2-api"
                     value={row.name}
                     hint={t('monitoring:checks:name_hint')}
                     invalid={show && errs.name !== 'none'}
                     error={errs.name === 'duplicate'
                              ? t('monitoring:checks:err_name_duplicate')
                              : t('monitoring:checks:err_name')}
                     setValue={(v) => props.update({ name: v })} />

      <SettingsInput label={t('monitoring:checks:url')}
                     required={true}
                     placeholder="https://core2.example/health"
                     value={row.url}
                     hint={t('monitoring:checks:url_hint')}
                     invalid={show && errs.url !== 'none'}
                     error={t('monitoring:checks:err_url')}
                     setValue={(v) => props.update({ url: v })} />

      <FlexRow align="start" className="gap-[12px] w-full flex-wrap">
        <SettingsSelect label={t('monitoring:checks:method')}
                        value={row.method}
                        options={methodOptions}
                        className="!w-[160px]"
                        setValue={(v) => props.update({ method: v })} />

        <SettingsInput label={t('monitoring:checks:expect_status')}
                       value={row.expectStatus}
                       placeholder={String(EXPECT_STATUS_DEFAULT)}
                       className="!w-[160px]"
                       invalid={show && errs.expectStatus}
                       error={t('monitoring:checks:err_expect_status')}
                       setValue={(v) => props.update({ expectStatus: v })} />

        <SettingsInput label={t('monitoring:checks:interval')}
                       value={row.intervalSeconds}
                       placeholder={t('monitoring:checks:interval_placeholder')}
                       className="!w-[220px]"
                       invalid={show && errs.intervalSeconds}
                       error={t('monitoring:checks:err_interval')}
                       setValue={(v) => props.update({ intervalSeconds: v })} />
      </FlexRow>

      <SettingsInput label={t('monitoring:checks:body_contains')}
                     value={row.bodyContains}
                     hint={t('monitoring:checks:body_contains_hint')}
                     setValue={(v) => props.update({ bodyContains: v })} />

      {/* Warn BEFORE the save. The server reports it on the row afterwards, but
          by then the operator is watching a check that can never pass. */}
      { bodyContainsUnreachable(row) &&
        <InfoBanner tone="warn" text={t('monitoring:checks:head_body_warning')} />
      }
    </FlexRow>
  </FlexRow>
  );
};

// ---------------------------------------------------------------------------
// "Send test card" / "Test connection" — one button plus its inline result
// ---------------------------------------------------------------------------

interface TestButtonProps {
  caption:		string;
  testingCaption:	string;
  icon:			string;
  okLabel:		string;
  failedLabel:	string;
  saveFirst:		string;
  enabled:		boolean;
  busy:			boolean;
  unsaved:		boolean;
  result:		TestOutcome | null;
  onClick:		() => any;
}

const TestButton = (props: TestButtonProps) => (
  <FlexRow align="center" className="gap-[12px] w-full flex-wrap">
    <FlexRow flex={0}>
      <Button caption={props.busy ? props.testingCaption : props.caption}
              icon={props.icon}
              layout="white"
              isDisabled={!props.enabled}
              onClick={() => props.onClick()} />
    </FlexRow>

    {/* The test always runs on what is SAVED, so with edits pending it would
        test something other than what is on screen. */}
    { props.unsaved &&
      <span className={su.hint}>{ props.saveFirst }</span>
    }

    {/* Repeated inline, on top of the toast: the toast goes away, and the
        reason a card was not delivered has to stay readable. */}
    { !props.unsaved && !props.busy && props.result &&
      <FlexRow flex={1} className="gap-[8px] items-center min-w-[240px]">
        <Pill tone={props.result.ok ? 'ok' : 'bad'}
              dot={true}
              label={props.result.ok ? props.okLabel : props.failedLabel} />
        { props.result.text &&
          <span className={'text-[13px] leading-[18px] ' +
                           (props.result.ok ? 'text-[#2E7D4F]' : 'text-[#B14545]')}>
            { props.result.text }
          </span>
        }
      </FlexRow>
    }
  </FlexRow>
);

// ---------------------------------------------------------------------------

const Monitoring = () => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);

  const [status, setStatus] = React.useState(null as MonitoringStatus | null);
  const [statusFailed, setStatusFailed] = React.useState(false);

  const [cfg, setCfg] = React.useState(null as MonitoringConfig | null);
  const [cfgError, setCfgError] = React.useState('' as ConfigError);

  const [form, setForm] = React.useState(EMPTY_FORM);
  const [savedForm, setSavedForm] = React.useState(EMPTY_FORM);

  const [triedSave, setTriedSave] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState('');

  const [testing, setTesting] = React.useState('' as TestChannel);
  const [teamsResult, setTeamsResult] = React.useState(null as TestOutcome | null);
  const [ticketsResult, setTicketsResult] = React.useState(null as TestOutcome | null);

  // Bumped on every edit of a channel's card, so a test answer that arrives
  // after the admin moved on is dropped instead of describing old values.
  const teamsEditRef = React.useRef(0);
  const ticketsEditRef = React.useRef(0);

  const recheckRef = React.useRef(0 as any);

  const admin = !!role?.admin;

  // §9: the Teams / tickets / server-name editors only once a status answer
  // proves the server masks the secrets. Until then the page behaves as before.
  const channels = !!status?.has_hostname;

  // A bare fetch, on purpose. Routed through apiOperation a 403/503 would raise
  // an undismissable red toast on EVERY poll, and a 401 on an idled-out session
  // would force-log-out the admin mid-edit. The endpoint returns a raw object
  // rather than the {errors:[...]} envelope too, so the helper has nothing to
  // report on success either.
  const loadStatus = React.useCallback(async () => {
    try {
      const res = await fetch(monitoringStatusUrl, { method: 'GET' });

      if (!res.ok) {
        setStatusFailed(true);
        return;
      }

      // Keep the LAST GOOD snapshot on screen through a failure; only replace
      // it with something the server actually answered.
      setStatus(copyMonitoringStatus(await res.json()));
      setStatusFailed(false);
    } catch (e) {
      console.error('Monitoring/loadStatus/failed', e);
      setStatusFailed(true);
    }
  }, []);

  // Same reasoning, plus one of its own: 503 here means "monitoring is not
  // available on this node", which is a state to explain inline, not an error
  // to shout about.
  const loadConfig = React.useCallback(async () => {
    try {
      const res = await fetch(monitoringConfigUrl, { method: 'GET' });

      if (!res.ok) {
        // 500 with this body is a hand-edited config.json whose `monitoring`
        // is not an object — nothing the page can repair, so say what to fix.
        const broken =	res.status === 500 &&
        				/not a JSON object/i.test(await res.text().catch(() => ''));

        setCfgError(	res.status === 403	?	'forbidden'
        				:	res.status === 503	?	'unavailable'
        				:	broken				?	'broken'
        				:	'failed');
        return;
      }

      const loaded = copyMonitoringConfig(await res.json());
      const next = toForm(loaded);

      setCfg(loaded);
      setForm(next);
      setSavedForm(next);
      setCfgError('');
    } catch (e) {
      console.error('Monitoring/loadConfig/failed', e);
      setCfgError('failed');
    }
  }, []);

  // Once on mount and after a save — never polled. Polling it would fight the
  // admin's typing.
  React.useEffect(() => {
    if (admin)
      void loadConfig();
  }, [admin, loadConfig]);

  // `changes: false` deliberately: unlike the EPG page the health half here is
  // read-only and independent of the settings buffer, so a pending edit is no
  // reason to stop watching. The visibility gate inside still applies — every
  // poll is an authenticated request that refreshes the session's idle
  // deadline, and a forgotten background tab must not hold it open.
  React.useEffect(
    () => startStatusPoll({
      admin:		admin,
      changes:		false,
      load:			loadStatus,
      isVisible:	() => document.visibilityState === 'visible',
      intervalMs:	MONITORING_POLL_MS,
    }),
    [admin, loadStatus]
  );

  React.useEffect(() => {
    return () => {
      if (recheckRef.current)
        clearTimeout(recheckRef.current);
    };
  }, []);

  const patchForm = React.useCallback((patch: Partial<MonitoringForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    // The previous save's message describes the previous save. Once the form
    // moves it is no longer about what is on screen.
    setSaveMessage('');
  }, []);

  // A test result describes the values that were on screen when it ran; once
  // a field of that card moves it no longer does.
  const patchTeams = React.useCallback((patch: Partial<MonitoringForm>) => {
    teamsEditRef.current++;
    setTeamsResult(null);
    patchForm(patch);
  }, [patchForm]);

  const patchTickets = React.useCallback((patch: Partial<MonitoringForm>) => {
    ticketsEditRef.current++;
    setTicketsResult(null);
    patchForm(patch);
  }, [patchForm]);

  // No payload: both endpoints test what is SAVED. The answer can take up to
  // 15 s. apiOperation raises the toast; the result is also kept inline.
  const runTest = React.useCallback(async (which: 'teams' | 'tickets') => {
    const editRef = which === 'teams' ? teamsEditRef : ticketsEditRef;
    const setResult = which === 'teams' ? setTeamsResult : setTicketsResult;
    const startedAt = editRef.current;

    setResult(null);
    setTesting(which);

    let outcome: TestOutcome;
    try {
      const res = await apiOperation('POST', which === 'teams' ? monitoringTestAlertUrl : monitoringTestTicketsUrl);

      outcome =	res.result
      			?	testOutcome(res.result)
      			:	{ ok: false, text: ('HTTP ' + res.response.status + ' ' + res.response.statusText).trim() };
    } catch {
      outcome = { ok: false, text: '' };
    } finally {
      setTesting('');
    }

    if (editRef.current === startedAt)
      setResult(outcome);
  }, []);

  const handleSave = React.useCallback(async () => {
    if (!cfg)
      return;

    if (formInvalid(form, cfg.monitoring, channels)) {
      setTriedSave(true);
      return;
    }

    // Whole-section REPLACE, not a pointer patch: the section is built from the
    // object GET returned, so `overrides[]` and any key a newer server added
    // survive. Building it from the fields this form renders would silently
    // delete them.
    const payload = buildMonitoringPayload(
      buildSection(cfg.monitoring, form, channels),
      logKeyPayload(form.logLevel, cfg.log_level, LOG_LEVEL_NORMAL),
      logKeyPayload(form.logFormat, cfg.log_format, LOG_FORMAT_TEXT),
    );

    setSaving(true);
    try {
      const res = await apiOperation('POST', monitoringConfigUrl, payload);
      const mess = res.result;

      // Branch on code === 0, never on the presence of `error` — on success
      // the message still arrives in a field called `error`. A non-zero code
      // wrote nothing, and apiOperation has already surfaced it.
      if (!checkNoError(mess))
        return;

      setSaveMessage(mess.errors[0].error || '');
      setTriedSave(false);

      await loadConfig();

      if (recheckRef.current)
        clearTimeout(recheckRef.current);

      recheckRef.current = setTimeout(() => void loadStatus(), SAVE_RECHECK_MS);
    } finally {
      setSaving(false);
    }
  }, [form, cfg, channels, loadConfig, loadStatus]);

  const header = (
    <FlexRow direction="column" align="start" className="pb-[4px]">
      <span className={su.pageTitle}>{ t('monitoring:caption') }</span>
    </FlexRow>
  );

  // The route is not admin-gated — only the sidebar link is — so the page has
  // to gate itself. There is no ViewMonitoring privilege: admin or nothing.
  if (!admin) {
    return (
      <div className={su.page}>
        <div className={su.pageInner}>
          { header }
          <SettingsCard icon="lock"
                        title={t('monitoring:caption')}
                        subtitle={t('monitoring:admin_only')} />
        </div>
      </div>
    );
  }

  // Below the early return, so nothing here may be a hook.
  const nowMs = Date.now();
  const verdict = pageVerdict(status, nowMs);
  const groups = groupChecks(status ? status.checks : []);
  const changes = formChanged(form, savedForm);
  const anyInvalid = formInvalid(form, cfg ? cfg.monitoring : {}, channels);
  const silent = !!cfg && nobodyTold(form);

  const loaded = cfg ? cfg.monitoring : {};

  const nameErr = serverNameError(form.serverName);
  // From the FIELD, not status.server_name — that trails a save by up to 15 s.
  const host = (status && status.hostname) || '…';

  const webhookErr = webhookError(form.teamsWebhook, loaded);
  const webhookSet = form.teamsWebhook.trim() !== '';
  const mentionErrs = mentionErrors(form.mentions);
  const anyMention = form.mentions.some((m) => m.value.trim() !== '');

  const ticketErrs = ticketsErrors(form, loaded);
  const ticketsPair = form.ticketsUrl.trim() !== '' && form.ticketsToken.trim() !== '';
  const tokenMasked = tokenUntouched(form.ticketsToken, loaded);

  // Paused channels and a switched-off server do NOT disable the tests: they
  // still run, and the server's reply says why no real alert goes out.
  const testIdle = !changes && !saving && !testing;
  const teamsTestable = testIdle && savedHasTeams(loaded);
  const ticketsTestable = testIdle && savedHasTickets(loaded);

  const mentionError = (i: number) => {
    switch (mentionErrs[i]) {
    case 'duplicate':	return t('monitoring:teams:err_mentions_duplicate');
    case 'many':		return t('monitoring:teams:err_mentions_many');
    default:			return t('monitoring:teams:err_mention');
    }
  };

  const ticketsFieldError = (err: string, own: string) =>
    	err === 'pair'		?	t('monitoring:tickets:err_pair')
    :	err === 'repaste'	?	t('monitoring:tickets:err_repaste')
    :	own;
  const badEmails = skippedEmails(form.emails);

  const sweepAge = status ? secondsSince(status.last_sweep_at, nowMs) : -1;
  const sweepIsStale = verdict.kind === 'stale';

  const levelOptions: FieldInfo[] = LOG_LEVELS_SHOWN.map((value) => ({
    value:	value,
    desc:	t('monitoring:log:level_' + value),
  }));

  const formatOptions: FieldInfo[] = LOG_FORMATS_SHOWN.map((value) => ({
    value:	value,
    desc:	t('monitoring:log:format_' + value),
  }));

  // A level a human set in config.json by hand (`warn`, `error`) is offered as
  // a third option rather than silently reset to Normal by the next save.
  const extraLevel = cfg ? extraStoredOption(cfg.log_level, LOG_LEVELS_SHOWN) : '';
  if (extraLevel)
    levelOptions.push({ value: extraLevel, desc: t('monitoring:log:server_set', { value: extraLevel }) });

  const extraFormat = cfg ? extraStoredOption(cfg.log_format, LOG_FORMATS_SHOWN) : '';
  if (extraFormat)
    formatOptions.push({ value: extraFormat, desc: t('monitoring:log:server_set', { value: extraFormat }) });

  const formatChanged = !!cfg && form.logFormat !== savedForm.logFormat;

  return (
  <React.Fragment>
    <div className={su.page}>
      <div className={su.pageInner}>
        { header }

        {/* --- 1. The verdict, before any detail ------------------------- */}
        <SettingsCard icon={VERDICT_ICON[verdict.kind]}
                      title={t('monitoring:verdict:' + verdict.kind)}
                      subtitle={t('monitoring:verdict:' + verdict.kind + '_hint')}
                      status={
                        <Pill tone={VERDICT_TONE[verdict.kind]}
                              dot={true}
                              label={t('monitoring:verdict:counts', {
                                total:		verdict.total,
                                failing:	verdict.failing,
                                degraded:	verdict.degraded,
                              })} />
                      }>

          {/* Not an error state. On a multi-server deployment only one server
              runs the checks, so an idle node is the expected shape. */}
          { verdict.kind === 'idle' &&
            <InfoBanner text={t('monitoring:verdict:idle_explain')} />
          }

          { sweepIsStale &&
            <InfoBanner tone="warn" text={t('monitoring:verdict:stale_explain')} />
          }

          {/* Silence must not look like health — a green verdict with nobody
              listening is the worst thing this page could imply. */}
          { silent &&
            <InfoBanner tone="warn" text={t('monitoring:verdict:log_only')} />
          }

          {/* One inline note, not a toast storm — and the last good snapshot
              stays on screen underneath it. */}
          { statusFailed &&
            <InfoBanner tone="warn"
                        text={status
                                ? t('monitoring:status:poll_failed_stale')
                                : t('monitoring:status:poll_failed')} />
          }
        </SettingsCard>

        {/* --- 2. Current health ---------------------------------------- */}
        { !!groups.length &&
          <React.Fragment>
            <GroupDivider caption={t('monitoring:section:health')} />

            { groups.map((group) =>
              <SettingsCard key={group.family}
                            icon={FAMILY_ICON[group.family]}
                            title={t('monitoring:family:' + group.family)}
                            subtitle={t('monitoring:family:' + group.family + '_hint')}
                            status={
                              <Pill tone={worstTone(group.rows)}
                                    dot={true}
                                    label={t('monitoring:family:count', { count: group.rows.length })} />
                            }>
                { group.rows.map((row) =>
                  <HealthRow key={row.check} row={row} />
                )}
              </SettingsCard>
            )}
          </React.Fragment>
        }

        {/* --- 3. Settings ---------------------------------------------- */}
        <GroupDivider caption={t('monitoring:section:settings')} />

        { !!cfgError &&
          <InfoBanner tone="warn" text={t('monitoring:config:error_' + cfgError)} />
        }

        { !!cfg &&
        <React.Fragment>

          {/* The log control leads deliberately: it was originally last, below
              six health cards, and was effectively undiscoverable there. */}
          <SettingsCard icon="article"
                        title={t('monitoring:log:caption')}
                        subtitle={t('monitoring:log:subtitle')}>

            <FlexRow align="start" className="gap-[12px] w-full flex-wrap">
              <SettingsSelect label={t('monitoring:log:level')}
                              value={form.logLevel}
                              options={levelOptions}
                              hint={t('monitoring:log:level_hint')}
                              className="!w-[320px]"
                              setValue={(v) => patchForm({ logLevel: v })} />

              <SettingsSelect label={t('monitoring:log:format')}
                              value={form.logFormat}
                              options={formatOptions}
                              hint={t('monitoring:log:format_hint')}
                              className="!w-[320px]"
                              setValue={(v) => patchForm({ logFormat: v })} />
            </FlexRow>

            { form.logLevel === LOG_LEVEL_VERBOSE &&
              <InfoBanner tone="warn" text={t('monitoring:log:debug_warning')} />
            }

            {/* The logger is built once at boot, so a saved format keeps
                emitting the old shape until the service restarts. Repeat it
                here or an admin watches for JSON lines that cannot arrive. */}
            { formatChanged && form.logFormat === LOG_FORMAT_JSON &&
              <InfoBanner tone="warn" text={t('monitoring:log:restart_warning')} />
            }

            <InfoBanner text={t('monitoring:log:audit_hint')} />
          </SettingsCard>

          {/* --- Who is told ------------------------------------------- */}
          <SettingsCard icon="notifications"
                        title={t('monitoring:alerts:caption')}
                        subtitle={t('monitoring:alerts:subtitle')}>

            { form.emails.map((row, i) =>
              <FlexRow key={row.id} align="start" className="gap-[10px] w-full">
                <SettingsInput label={t('monitoring:alerts:email')}
                               value={row.value}
                               placeholder="ops@example.com"
                               className="flex-1"
                               invalid={emailIsUrl(row.value)}
                               error={t('monitoring:alerts:err_email_is_url')}
                               setValue={(v) => patchForm({
                                 emails: form.emails.map((e, j) =>
                                   (j === i ? { ...e, value: v } : e)),
                               })} />
                <FlexRow flex={0}
                         className="gap-[6px] cursor-pointer select-none h-[52px]"
                         onClick={() => patchForm({
                           emails: form.emails.filter((_, j) => j !== i),
                         })}>
                  <span className="material-icons !text-[18px] !text-[#D64545]">delete_outline</span>
                </FlexRow>
              </FlexRow>
            )}

            { silent &&
              <InfoBanner tone="warn" text={t('monitoring:alerts:log_only')} />
            }

            <AddRow caption={t('monitoring:alerts:add')}
                    onClick={() => patchForm({ emails: [...form.emails, newEmailEdit()] })} />

            {/* The server SKIPS an unusable address instead of rejecting the
                save, so this warns and never blocks. */}
            { !!badEmails.length &&
              <InfoBanner tone="warn"
                          text={t('monitoring:alerts:skipped', { list: badEmails.join(', ') })} />
            }

            <SettingsInput label={t('monitoring:alerts:realert')}
                           value={form.realertHours}
                           placeholder={String(REALERT_HOURS_DEFAULT)}
                           className="!w-[320px]"
                           hint={t('monitoring:alerts:realert_hint', { n: REALERT_HOURS_DEFAULT })}
                           invalid={triedSave && intFieldInvalid(form.realertHours)}
                           error={t('monitoring:alerts:err_realert')}
                           setValue={(v) => patchForm({ realertHours: v })} />

            <InfoBanner text={t('monitoring:alerts:recovery_hint')} />
          </SettingsCard>

          {/* --- Microsoft Teams --------------------------------------- */}
          { channels &&
          <SettingsCard icon="forum"
                        title={t('monitoring:teams:caption')}
                        subtitle={t('monitoring:teams:subtitle')}>

            {/* A secret: the loaded mask goes back exactly as it is, and the
                field is kept away from autofill and cloud spell-checkers. */}
            <SettingsInput label={t('monitoring:teams:url')}
                           value={form.teamsWebhook}
                           placeholder="https://….logic.azure.com/workflows/…"
                           hint={t('monitoring:teams:url_hint')}
                           autoComplete="off"
                           spellCheck={false}
                           invalid={triedSave && webhookErr !== 'none'}
                           error={webhookErr === 'masked'
                                    ? t('monitoring:teams:err_masked')
                                    : t('monitoring:teams:err_url')}
                           setValue={(v) => patchTeams({ teamsWebhook: v })} />

            { webhookUntouched(form.teamsWebhook, loaded) &&
              <InfoBanner text={t('monitoring:teams:masked_hint')} />
            }

            { !webhookSet &&
              <InfoBanner text={t('monitoring:teams:none')} />
            }

            <ToggleRow value={form.teamsEnabled}
                       caption={t('monitoring:teams:enabled')}
                       description={t('monitoring:teams:enabled_hint')}
                       isDisabled={!webhookSet}
                       setValue={(v) => patchTeams({ teamsEnabled: v })} />

            { !form.teamsEnabled && webhookSet &&
              <InfoBanner tone="warn" text={t('monitoring:teams:paused_hint')} />
            }

            { form.mentions.map((row: EmailEdit, i: number) =>
              <FlexRow key={row.id} align="start" className="gap-[10px] w-full">
                <SettingsInput label={t('monitoring:teams:mention')}
                               value={row.value}
                               placeholder="Jane Doe <jane.doe@primetime.ch>"
                               className="flex-1"
                               hint={i === form.mentions.length - 1
                                       ? t('monitoring:teams:mention_hint')
                                       : undefined}
                               invalid={triedSave && mentionErrs[i] !== 'none'}
                               error={mentionError(i)}
                               setValue={(v) => patchTeams({
                                 mentions: form.mentions.map((m, j) =>
                                   (j === i ? { ...m, value: v } : m)),
                               })} />
                <FlexRow flex={0}
                         className="gap-[6px] cursor-pointer select-none h-[52px]"
                         onClick={() => patchTeams({
                           mentions: form.mentions.filter((_, j) => j !== i),
                         })}>
                  <span className="material-icons !text-[18px] !text-[#D64545]">delete_outline</span>
                </FlexRow>
              </FlexRow>
            )}

            { !anyMention &&
              <InfoBanner text={t('monitoring:teams:no_mentions')} />
            }

            <AddRow caption={t('monitoring:teams:add_mention')}
                    onClick={() => patchTeams({ mentions: [...form.mentions, newEmailEdit()] })} />

            <TestButton caption={t('monitoring:teams:test')}
                        testingCaption={t('monitoring:teams:testing')}
                        icon="send"
                        okLabel={t('monitoring:teams:test_ok')}
                        failedLabel={t('monitoring:teams:test_failed')}
                        saveFirst={t('monitoring:teams:save_first')}
                        enabled={teamsTestable}
                        busy={testing === 'teams'}
                        unsaved={changes}
                        result={teamsResult}
                        onClick={() => void runTest('teams')} />
          </SettingsCard>
          }

          {/* --- Tickets ----------------------------------------------- */}
          { channels &&
          <SettingsCard icon="confirmation_number"
                        title={t('monitoring:tickets:caption')}
                        subtitle={t('monitoring:tickets:subtitle')}>

            <SettingsInput label={t('monitoring:tickets:url')}
                           value={form.ticketsUrl}
                           placeholder="https://git.primetime.ch/tickets"
                           hint={t('monitoring:tickets:url_hint')}
                           spellCheck={false}
                           invalid={triedSave && ticketErrs.url !== 'none'}
                           error={ticketsFieldError(ticketErrs.url, t('monitoring:tickets:err_url'))}
                           setValue={(v) => patchTickets({ ticketsUrl: v })} />

            {/* Plain text while it holds the mask, so REDACTED reads as what
                it is; a password field once a real token is typed. */}
            <SettingsInput label={t('monitoring:tickets:token')}
                           value={form.ticketsToken}
                           type={tokenMasked ? 'text' : 'password'}
                           hint={t('monitoring:tickets:token_hint')}
                           autoComplete="new-password"
                           spellCheck={false}
                           invalid={triedSave && ticketErrs.token !== 'none'}
                           error={ticketsFieldError(ticketErrs.token, t('monitoring:tickets:err_token'))}
                           setValue={(v) => patchTickets({ ticketsToken: v })} />

            { tokenMasked &&
              <InfoBanner text={t('monitoring:tickets:masked_hint')} />
            }

            <SettingsInput label={t('monitoring:tickets:project')}
                           value={form.ticketsProject}
                           placeholder="primetime/products/middleware"
                           hint={t('monitoring:tickets:project_hint')}
                           spellCheck={false}
                           invalid={triedSave && ticketErrs.project}
                           error={t('monitoring:tickets:err_project')}
                           setValue={(v) => patchTickets({ ticketsProject: v })} />

            { !ticketsPair &&
              <InfoBanner text={t('monitoring:tickets:none')} />
            }

            <ToggleRow value={form.ticketsEnabled}
                       caption={t('monitoring:tickets:enabled')}
                       description={t('monitoring:tickets:enabled_hint')}
                       isDisabled={!ticketsPair}
                       setValue={(v) => patchTickets({ ticketsEnabled: v })} />

            { !form.ticketsEnabled && ticketsPair &&
              <InfoBanner tone="warn" text={t('monitoring:tickets:paused_hint')} />
            }

            <TestButton caption={t('monitoring:tickets:test')}
                        testingCaption={t('monitoring:tickets:testing')}
                        icon="link"
                        okLabel={t('monitoring:tickets:test_ok')}
                        failedLabel={t('monitoring:tickets:test_failed')}
                        saveFirst={t('monitoring:tickets:save_first')}
                        enabled={ticketsTestable}
                        busy={testing === 'tickets'}
                        unsaved={changes}
                        result={ticketsResult}
                        onClick={() => void runTest('tickets')} />
          </SettingsCard>
          }

          {/* --- Cadence ---------------------------------------------- */}
          <SettingsCard icon="schedule"
                        title={t('monitoring:timing:caption')}
                        subtitle={t('monitoring:timing:subtitle')}>

            <FlexRow align="start" className="gap-[12px] w-full flex-wrap">
              <SettingsInput label={t('monitoring:timing:interval')}
                             value={form.intervalSeconds}
                             placeholder={String(CHECK_INTERVAL_DEFAULT)}
                             className="!w-[260px]"
                             hint={t('monitoring:timing:interval_hint', { n: CHECK_INTERVAL_DEFAULT })}
                             invalid={triedSave && intFieldInvalid(form.intervalSeconds, 0)}
                             error={t('monitoring:timing:err_seconds')}
                             setValue={(v) => patchForm({ intervalSeconds: v })} />

              <SettingsInput label={t('monitoring:timing:timeout')}
                             value={form.timeoutSeconds}
                             placeholder={String(CHECK_TIMEOUT_DEFAULT)}
                             className="!w-[260px]"
                             hint={t('monitoring:timing:timeout_hint', { n: CHECK_TIMEOUT_DEFAULT })}
                             invalid={triedSave && intFieldInvalid(form.timeoutSeconds, 0)}
                             error={t('monitoring:timing:err_seconds')}
                             setValue={(v) => patchForm({ timeoutSeconds: v })} />
            </FlexRow>

            <InfoBanner text={t('monitoring:timing:skyway_hint', {
                          minutes:	SKYWAY_PROBE_INTERVAL_DEFAULT / 60,
                          seconds:	SKYWAY_PROBE_TIMEOUT_MIN,
                        })} />

            {/* Only intervals are settings. The detection thresholds — three
                consecutive failures, 45-minute stale auth, six hours of guide
                coverage — are fixed, so there is nothing to offer here. */}
            <InfoBanner text={t('monitoring:timing:thresholds_hint')} />

            {/* Per-check overrides have no editor yet, but they round-trip
                untouched through the save. Say so rather than let an operator
                who set one in config.json wonder if this page ate it. */}
            { !!(cfg.monitoring.overrides || []).length &&
              <InfoBanner text={t('monitoring:timing:overrides_hint', {
                            count: (cfg.monitoring.overrides || []).length,
                          })} />
            }
          </SettingsCard>

          {/* --- Operator's own services ------------------------------ */}
          <SettingsCard icon="lan"
                        title={t('monitoring:checks:caption')}
                        subtitle={t('monitoring:checks:subtitle')}>

            { form.checks.map((row, i) =>
              <CustomCheckRow key={row.id}
                              row={row}
                              index={i}
                              all={form.checks}
                              triedSave={triedSave}
                              update={(patch) => patchForm({
                                checks: form.checks.map((c, j) =>
                                  (j === i ? { ...c, ...patch } : c)),
                              })}
                              remove={() => patchForm({
                                checks: form.checks.filter((_, j) => j !== i),
                              })} />
            )}

            <AddRow caption={t('monitoring:checks:add')}
                    onClick={() => patchForm({ checks: [...form.checks, newCheckEdit()] })} />

            <InfoBanner text={t('monitoring:checks:limits_hint')} />
            <InfoBanner text={t('monitoring:checks:auth_hint')} />
          </SettingsCard>

          {/* --- Single-server switch --------------------------------- */}
          <SettingsCard icon="dns"
                        title={t('monitoring:server:caption')}
                        subtitle={t('monitoring:server:subtitle')}>

            { channels &&
              <SettingsInput label={t('monitoring:server:name')}
                             value={form.serverName}
                             placeholder="beta"
                             hint={t('monitoring:server:name_hint', {
                               label:	serverLabel(form.serverName, host),
                               host:	host,
                             })}
                             invalid={triedSave && nameErr !== 'none'}
                             error={nameErr === 'long'
                                      ? t('monitoring:server:err_name_long')
                                      : t('monitoring:server:err_name')}
                             setValue={(v) => patchForm({ serverName: v })} />
            }

            <ToggleRow value={!form.disabled}
                       caption={t('monitoring:server:enabled')}
                       description={t('monitoring:server:enabled_hint')}
                       setValue={(v) => patchForm({ disabled: !v })} />

            { form.disabled &&
              <InfoBanner text={t('monitoring:server:disabled_explain')} />
            }
          </SettingsCard>
        </React.Fragment>
        }

        {/* --- 4. Technical details ------------------------------------- */}
        { !!status &&
          <SettingsCard icon="terminal"
                        title={t('monitoring:tech:caption')}
                        subtitle={t('monitoring:tech:subtitle')}>

            <FlexRow className="gap-[24px] w-full flex-wrap">
              <FlexRow direction="column" align="start" flex={0} className="gap-[2px] min-w-[200px]">
                <span className={su.hint}>{ t('monitoring:tech:last_sweep') }</span>
                <span className="text-[14px] text-[#1F2733]">
                  { status.last_sweep_at
                      ? formatWhen(status.last_sweep_at) +
                        (sweepAge >= 0 ? ' (' + t('monitoring:tech:ago', { n: sweepAge }) + ')' : '')
                      : t('monitoring:tech:no_sweep') }
                </span>
              </FlexRow>

              <FlexRow direction="column" align="start" flex={0} className="gap-[2px] min-w-[160px]">
                <span className={su.hint}>{ t('monitoring:tech:log_drops') }</span>
                <span className="text-[14px] text-[#1F2733]">{ status.log_drops }</span>
              </FlexRow>

              <FlexRow direction="column" align="start" flex={0} className="gap-[2px] min-w-[160px]">
                <span className={su.hint}>{ t('monitoring:tech:pending') }</span>
                <span className="text-[14px] text-[#1F2733]">{ verdict.pending }</span>
              </FlexRow>

              <FlexRow direction="column" align="start" flex={0} className="gap-[2px] min-w-[200px]">
                <span className={su.hint}>{ t('monitoring:tech:generated_at') }</span>
                <span className="text-[14px] text-[#1F2733]">
                  { status.generated_at ? formatWhen(status.generated_at) : '—' }
                </span>
              </FlexRow>
            </FlexRow>

            { status.log_drops > 0 &&
              <InfoBanner tone="warn" text={t('monitoring:tech:log_drops_warning')} />
            }
          </SettingsCard>
        }
      </div>
    </div>

    { !!cfg &&
      <FlexRow className="sticky bottom-0 z-20 bg-white border-t-1 border-solid border-[#E6E9EF] px-[32px] py-[14px]">
        <FlexRow flex={1} className="gap-[8px] items-center flex-wrap">
          { changes &&
            <React.Fragment>
              <span className="material-icons !text-[18px] !text-[#B98B36]">edit</span>
              <span className="text-[13px] text-[#8A6A2F]">
                { t('monitoring:save:unsaved') }
              </span>
            </React.Fragment>
          }

          {/* code: 0 can still carry a "skipped:" clause — part of the save
              will not do anything — or the log-format restart note. A silent
              green toast there would be a lie, so the message is repeated
              inline and coloured by what it actually says. */}
          { !changes && !!saveMessage &&
            <FlexRow flex={1} className="gap-[8px] items-center">
              <span className={'material-icons !text-[18px] ' +
                               (messageIsQualified(saveMessage)
                                  ? '!text-[#B98B36]'
                                  : '!text-[#3BA05F]')}>
                { messageIsQualified(saveMessage) ? 'warning_amber' : 'check_circle' }
              </span>
              <span className={'text-[13px] ' +
                               (messageIsQualified(saveMessage)
                                  ? 'text-[#8A6A2F]'
                                  : 'text-[#2E7D4F]')}>
                { saveMessage }
              </span>
            </FlexRow>
          }
        </FlexRow>
        <FlexRow flex={0}>
          <Button caption={t('monitoring:save:caption')}
                  icon="save"
                  isDisabled={!changes || saving || (triedSave && anyInvalid)}
                  onClick={() => void handleSave()} />
        </FlexRow>
      </FlexRow>
    }
  </React.Fragment>
  );
};

export default Monitoring;
